import { DropZone } from '@design-system/components/dropZone/dropZone';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@mui/material';

type Format = 'image' | 'video' | 'audio';

const DEFAULT_WIDTH = 161;
const DEFAULT_HEIGHT = 221;

type MediaEditorProps = {
  accept: string;
  format: Format;
  src: string | null;
  onUpload?: (file: File) => void;
  onRemove?: () => void;
  loading: boolean;
  disabled: boolean;
  readOnly?: boolean;
  width?: number;
  height?: number;
};

export const MediaEditor = ({
  accept,
  format,
  src,
  loading,
  onUpload,
  onRemove,
  disabled,
  readOnly,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
}: MediaEditorProps) => {
  return (
    <div
      style={{ width, height }}
      className={clsx(
        sprinkles({ bg: src ? 'inherit' : 'N-10' }),
        'flex justify-center items-center px-2 py-4 rounded-lg',
      )}
    >
      {loading ? (
        <CircularProgress />
      ) : src ? (
        <Preview
          format={format}
          src={src}
          width={width}
          height={height}
          onRemove={readOnly ? undefined : onRemove}
        />
      ) : disabled || readOnly ? null : (
        <DropZone
          onChange={(files: File[]) => files[0] && onUpload?.(files[0])}
          accept={[accept]}
          className="text-center border-none"
          textClassName="text-[14px] font-medium leading-5"
          hideIcon={true}
          bgColor={sprinkles({ bg: 'N-10' })}
        />
      )}
    </div>
  );
};

const Preview = ({
  format,
  src,
  width,
  height,
  onRemove,
}: {
  format: Format;
  src: string;
  width: number;
  height: number;
  onRemove?: () => void;
}) => {
  switch (format) {
    case 'image':
      return (
        <div className="relative">
          <img
            src={src}
            style={{ width, height }}
            className={clsx(sprinkles({ clipPath: 'm' }), 'object-cover')}
            data-testid="workoutThumbnail"
          />
          {onRemove && (
            <FontAwesomeIcon
              icon={faCircleXmark}
              className="absolute top-[8px] right-[8px] text-[20px] text-white text-opacity-60 cursor-pointer"
              onClick={onRemove}
            />
          )}
        </div>
      );
    case 'video':
      return (
        <div className="relative">
          <video
            src={src}
            style={{ width, height }}
            className={clsx(sprinkles({ clipPath: 'sm' }), 'object-cover')}
            controls
            data-testid="workoutIntro"
          />
          {onRemove && (
            <FontAwesomeIcon
              icon={faCircleXmark}
              className="absolute top-[8px] right-[8px] text-[20px] text-white text-opacity-60 cursor-pointer"
              onClick={onRemove}
            />
          )}
        </div>
      );
  }
};
